import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import TimerIcon from '@mui/icons-material/Timer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const Rules = () => {
  const ruleHighlights = [
    {
      icon: <GavelIcon sx={{ fontSize: 50 }} />,
      title: 'Submission Focused',
      description: 'Victory comes through submission & points - pure grappling.',
    },
    {
      icon: <TimerIcon sx={{ fontSize: 50 }} />,
      title: 'Fast-Paced',
      description: 'High-energy matches designed to keep the action intense and spectators engaged.',
    },
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 50 }} />,
      title: 'Team Competition',
      description: 'Compete as part of a team. Individual victories contribute to team success.',
    },
  ];

  const handleDownload = () => {
    window.open('/NGF_Rulebook.pdf', '_blank');
  };

  const handleDownloadForm = () => {
    window.open('https://forms.gle/kcrUiY5xKfvy6Wky6', '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Official Rules - National Gauntlet Federation | NGF</title>
        <meta
          name="description"
          content="Download the official NGF rulebook. Learn about submission-focused competition rules, match formats, team scoring, and regulations for the National Gauntlet Federation."
        />
        <meta
          name="keywords"
          content="NGF rules, grappling rules, submission rules, competition rules, tournament regulations, NGF rulebook"
        />
        <link rel="canonical" href="https://ngfederation.com/rules" />
      </Helmet>

      <Box
        sx={{
          minHeight: 'calc(100vh - 200px)',
          py: { xs: 6, md: 10 },
          backgroundColor: 'background.paper',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '6px',
            background: 'linear-gradient(90deg, #0D47A1 0%, #D32F2F 50%, #0D47A1 100%)',
          }}
        />

        <Container maxWidth="lg">
          {/* Header Section */}
          <Box
            sx={{
              textAlign: 'center',
              mb: 8,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                color: 'primary.main',
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                animation: 'fadeInDown 0.8s ease-out',
                '@keyframes fadeInDown': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(-30px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              OFFICIAL RULES
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'text.secondary',
                maxWidth: 700,
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.5rem' },
              }}
            >
              A new standard for competitive grappling
            </Typography>
          </Box>

          {/* Download Section */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {/* Rulebook */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 6 },
                  border: '3px solid',
                  borderColor: 'secondary.main',
                  backgroundColor: 'white',
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DescriptionIcon sx={{ fontSize: 50, color: 'white' }} />
                  </Box>
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    color: 'text.primary',
                    fontWeight: 600,
                  }}
                >
                  NGF Official Rulebook
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    color: 'text.secondary',
                    flex: 1,
                  }}
                >
                  Download the complete rulebook to understand match formats, scoring systems,
                  legal techniques, team competitions, and all official regulations.
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  sx={{
                    py: 2,
                    px: 6,
                    fontSize: '1.1rem',
                    backgroundColor: 'secondary.main',
                    '&:hover': {
                      backgroundColor: 'secondary.dark',
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Download Rulebook (PDF)
                </Button>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 2,
                    color: 'text.secondary',
                  }}
                >
                  Version 1.0 | Last Updated: Spring 2026
                </Typography>
              </Paper>
            </Grid>

            {/* Grappler Form */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 6 },
                  border: '3px solid',
                  borderColor: 'primary.main',
                  backgroundColor: 'white',
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      backgroundColor: 'secondary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DescriptionIcon sx={{ fontSize: 50, color: 'white' }} />
                  </Box>
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    color: 'text.primary',
                    fontWeight: 600,
                  }}
                >
                  Grappler Form
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    color: 'text.secondary',
                    flex: 1,
                  }}
                >
                  All competing grapplers must complete and sign this required form before
                  participating in NGF tournaments. Review and submit to compete.
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DescriptionIcon />}
                  onClick={handleDownloadForm}
                  sx={{
                    py: 2,
                    px: 6,
                    fontSize: '1.1rem',
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Complete Form
                </Button>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 2,
                    color: 'text.secondary',
                  }}
                >
                  Version 1.0 | Last Updated: Spring 2026
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Rule Highlights */}
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              mb: 5,
              color: 'text.primary',
              fontWeight: 600,
            }}
          >
            Key Highlights
          </Typography>

          <Grid container spacing={4} sx={{ mb: 6 }}>
            {ruleHighlights.map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    textAlign: 'center',
                    backgroundColor: 'white',
                    border: '2px solid',
                    borderColor: 'transparent',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: index % 2 === 0 ? 'primary.main' : 'secondary.main',
                      transform: 'translateY(-8px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      backgroundColor: index % 2 === 0 ? 'primary.main' : 'secondary.main',
                      color: 'white',
                      mb: 3,
                    }}
                  >
                    {item.icon}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      mb: 2,
                      color: 'text.primary',
                      fontWeight: 600,
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.8,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Additional Info */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              backgroundColor: 'rgba(13, 71, 161, 0.05)',
              border: '2px solid',
              borderColor: 'primary.main',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                color: 'primary.main',
                fontWeight: 600,
              }}
            >
              Rules Philosophy
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.8,
              }}
            >
              The NGF ruleset is designed to promote exciting, submission-focused grappling while
              maintaining athlete safety. Our rules eliminate stalling, encourage aggressive
              technique, and create an environment where both competitors and spectators can
              appreciate the art of grappling at its highest level. Every rule is crafted to
              support our mission of making competitive grappling accessible, entertaining, and
              family-friendly.
            </Typography>
          </Paper>
        </Container>

        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(13, 71, 161, 0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
            display: { xs: 'none', md: 'block' },
          }}
        />
      </Box>
    </>
  );
};

export default Rules;